import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useGetTelehealthSessionInfo } from '../../../hooks';
import { useTrackEvent } from '../../../segment/segment';
import { EventTypeEnum, TrackParams } from '../../../segment/types';

/**
 * Small wrapper around useTrackEvent that adds properties for the current
 * patient, provider, and appointment (if available) to each event callback.
 */
export function useTrackIntroRoomEvent() {
  const { providerShortId, patientShortId } = useParams();
  const { data, loading } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId;
  const {
    formSubmitted: formSubmittedBase,
    userViewed: userViewedBase,
    userClicked: userClickedBase,
  } = useTrackEvent();

  const trackEvent = useCallback(
    (baseTrackEventCallback: typeof formSubmittedBase | typeof userViewedBase | typeof userClickedBase) => {
      return (event: EventTypeEnum, properties?: Partial<TrackParams>) => {
        if (loading || !patientShortId || !providerShortId) {
          return;
        }
        const mergedProperties = {
          patientShortId,
          providerShortId,
          appointmentShortId,
          ...properties,
        };
        // baseTrackEventCallback is a union of three generic <P>(type, props) callbacks,
        // which TS narrows to an unsatisfiable intersection of every event's required
        // props. The wrapper accepts Partial<TrackParams> at its boundary; per-event
        // shape correctness is enforced at the call sites that pass `properties`.
        (baseTrackEventCallback as (event: EventTypeEnum, properties: typeof mergedProperties) => void)(
          event,
          mergedProperties,
        );
      };
    },
    [appointmentShortId, loading, patientShortId, providerShortId],
  );

  return useMemo(
    () => ({
      formSubmitted: trackEvent(formSubmittedBase),
      userViewed: trackEvent(userViewedBase),
      userClicked: trackEvent(userClickedBase),
    }),
    [trackEvent, formSubmittedBase, userViewedBase, userClickedBase],
  );
}
